import React, { useCallback, useState } from 'react';
import { getAuthorizationReturn } from 'utils/authorizationReturn';
import { FragmentEnv } from 'utils/env';
import { useAuth0 } from '@auth0/auth0-react';

import { InlineLogo } from '@fragment/ui/src/components/Logo/Logo';

import { ConnectionLayout } from './ConnectionLayout';
import { ConnectionSignIn } from './ConnectionSignIn';

type LoadingProps = {
  loading?: boolean;
};

type Props =
  | (LoadingProps & {
      message?: string;
      errorMessage?: never;
    })
  | (LoadingProps & {
      message?: never;
      errorMessage?: string;
    });

export const LoginView = ({
  loading = false,
  errorMessage,
  message,
}: Props) => {
  const { loginWithRedirect } = useAuth0();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [signInError, setSignInError] = useState<string | undefined>();
  const handler = useCallback(async () => {
    setIsRedirecting(true);
    setSignInError(undefined);
    try {
      const authorizationReturn = getAuthorizationReturn(
        `${window.location.pathname}${window.location.search}`
      );
      await loginWithRedirect({
        ...(authorizationReturn
          ? { appState: { returnTo: authorizationReturn } }
          : {}),
        authorizationParams: {
          audience: FragmentEnv.auth0.globalApiAudience,
          connection: 'google-oauth2',
          scope: 'openid email profile offline_access',
        },
      });
    } catch {
      setIsRedirecting(false);
      setSignInError('Unable to sign in with Google. Please try again.');
    }
  }, [loginWithRedirect]);

  return (
    <ConnectionLayout title={<InlineLogo />}>
      <ConnectionSignIn
        isLoading={loading || isRedirecting}
        message={message}
        errorMessage={signInError ?? errorMessage}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Auth0 handles the redirect.
        onContinue={handler}
      />
    </ConnectionLayout>
  );
};
